import type { ReadingFixationRecord } from "types/tests";
import type { ReadingLayoutWord } from "@components/common/ReadingLayoutRenderer";

interface WordRect {
  index: number;
  x01Left: number;
  x01Right: number;
  y01Top: number;
  y01Bottom: number;
}

function buildWordRects(words: ReadingLayoutWord[]): WordRect[] {
  return words
    .filter((w) => w.r && Number.isFinite(w.r.x))
    .map((w) => ({
      index: w.i,
      x01Left: w.r.x,
      x01Right: w.r.x + w.r.w,
      y01Top: 1 - (w.r.y + w.r.h),
      y01Bottom: 1 - w.r.y,
    }));
}

function findClosestWord(
  fx01: number,
  fy01: number,
  wordRects: WordRect[],
  maxDistance01: number,
): number | null {
  let bestIdx: number | null = null;
  let bestDist = maxDistance01;

  for (const wr of wordRects) {
    const cx = (wr.x01Left + wr.x01Right) / 2;
    const cy = (wr.y01Top + wr.y01Bottom) / 2;
    const dx = Math.max(0, Math.abs(fx01 - cx) - (wr.x01Right - wr.x01Left) / 2);
    const dy = Math.max(0, Math.abs(fy01 - cy) - (wr.y01Bottom - wr.y01Top) / 2);
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = wr.index;
    }
  }

  return bestIdx;
}

function getFixationWordIndex(
  fixation: ReadingFixationRecord,
  wordRects: WordRect[],
  maxDistance01: number,
): number | null {
  if (!fixation.position) return null;

  const fx01 = fixation.position.xPercentage / 100;
  const fy01 = fixation.position.yPercentage / 100;
  return findClosestWord(fx01, fy01, wordRects, maxDistance01);
}

function getMaxWordAssignmentDistance(wordRects: WordRect[]): number {
  const avgHeight =
    wordRects.reduce((sum, w) => sum + (w.y01Bottom - w.y01Top), 0) / wordRects.length;
  return avgHeight * 1.5;
}

export function aggregateGazeTimePerWord(
  fixations: ReadingFixationRecord[],
  words: ReadingLayoutWord[],
): Record<number, number> {
  const wordRects = buildWordRects(words);
  if (!wordRects.length) return {};

  const maxDist = getMaxWordAssignmentDistance(wordRects);

  const gazeTime: Record<number, number> = {};

  for (const fix of fixations) {
    if (!fix.position || !Number.isFinite(fix.durationSec)) continue;
    const wordIdx = getFixationWordIndex(fix, wordRects, maxDist);
    if (wordIdx !== null) {
      gazeTime[wordIdx] = (gazeTime[wordIdx] || 0) + fix.durationSec;
    }
  }

  return gazeTime;
}

export function gazeTimeToColorMap(gazeTimeByWord: Record<number, number>): Record<number, string> {
  const times = Object.values(gazeTimeByWord).filter((t) => t > 0);
  if (!times.length) return {};

  times.sort((a, b) => a - b);
  const p90 = times[Math.floor(times.length * 0.9)] || times[times.length - 1];
  const minTime = times[0];
  const range = p90 - minTime;

  const result: Record<number, string> = {};
  for (const [idx, time] of Object.entries(gazeTimeByWord)) {
    if (time <= 0) continue;
    const normalized = range > 0 ? Math.min(1, (time - minTime) / range) : 0;
    if (normalized < 0.05) continue;
    result[Number(idx)] = heatColor(normalized);
  }
  return result;
}

function heatColor(t: number): string {
  const hue = 60 - t * 60;
  const saturation = 80 + t * 15;
  const lightness = 92 - t * 35;
  const alpha = 0.3 + t * 0.55;
  return `hsla(${Math.round(hue)}, ${Math.round(saturation)}%, ${Math.round(lightness)}%, ${alpha.toFixed(2)})`;
}
